/**
 * Store Applications Management - Redesigned with EntityManagement design system
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InboxIcon from '@mui/icons-material/Inbox';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import storeService, { StoreApplication, ApplicationStatus } from '../services/storeService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';

import './EntityManagement.css';
import AssigneeCell from '../components/common/AssigneeCell';

const statusLabels: Record<string, string> = {
  [ApplicationStatus.PENDING]: 'در انتظار بررسی',
  [ApplicationStatus.APPROVED]: 'تایید شده',
  [ApplicationStatus.REJECTED]: 'رد شده'
};

const statusTagClass: Record<string, string> = {
  [ApplicationStatus.PENDING]: 'tag tag-sm tag-warning',
  [ApplicationStatus.APPROVED]: 'tag tag-sm tag-success',
  [ApplicationStatus.REJECTED]: 'tag tag-sm tag-danger'
};

const StoreApplicationsManagement: React.FC = () => {
  const [applications, setApplications] = useState<StoreApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<ApplicationStatus | ''>('');

  // Stats (independent totals)
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [currentApp, setCurrentApp] = useState<StoreApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState<string | null>(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await storeService.getStoreApplications(page, rowsPerPage, {
        status: filterStatus || undefined,
        storeName: searchTerm || undefined
      });
      setApplications(response.items || []);
      setTotalItems(response.meta?.totalItems || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterStatus, searchTerm]);

  // Fetch aggregated stats for cards (independent of current filter)
  const fetchStats = useCallback(async () => {
    try {
      const [all, pending, approved, rejected] = await Promise.all([
        storeService.getStoreApplications(0, 1, {}),
        storeService.getStoreApplications(0, 1, { status: ApplicationStatus.PENDING }),
        storeService.getStoreApplications(0, 1, { status: ApplicationStatus.APPROVED }),
        storeService.getStoreApplications(0, 1, { status: ApplicationStatus.REJECTED })
      ]);
      setStats({
        total: all.meta?.totalItems || 0,
        pending: pending.meta?.totalItems || 0,
        approved: approved.meta?.totalItems || 0,
        rejected: rejected.meta?.totalItems || 0
      });
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatDate = (d: string) => new Date(d).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleSearch = () => { setSearchTerm(searchInput); setPage(0); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const openDetails = (app: StoreApplication) => { setCurrentApp(app); setDetailsOpen(true); };
  const openApprove = (app: StoreApplication) => { setCurrentApp(app); setOperationSuccess(null); setApproveOpen(true); };
  const openReject = (app: StoreApplication) => { setCurrentApp(app); setRejectionReason(''); setOperationSuccess(null); setRejectOpen(true); };
  const closeAll = () => { setDetailsOpen(false); setApproveOpen(false); setRejectOpen(false); setCurrentApp(null); };

  const clearFilters = () => { setSearchTerm(''); setSearchInput(''); setFilterStatus(''); setPage(0); };

  const handleApprove = async () => {
    if (!currentApp) return;
    setIsApproving(true);
    try {
      await storeService.approveStoreApplication(currentApp._id);
      setOperationSuccess(`درخواست "${currentApp.storeName}" تایید شد`);
      setTimeout(() => { closeAll(); fetchApplications(); fetchStats(); }, 1000);
    } catch (e) { console.error(e); }
    finally { setIsApproving(false); }
  };

  const handleReject = async () => {
    if (!currentApp || !rejectionReason.trim()) return;
    setIsRejecting(true);
    try {
      await storeService.rejectStoreApplication(currentApp._id, rejectionReason);
      setOperationSuccess(`درخواست "${currentApp.storeName}" رد شد`);
      setTimeout(() => { closeAll(); fetchApplications(); fetchStats(); }, 1000);
    } catch (e) { console.error(e); }
    finally { setIsRejecting(false); }
  };

  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalItems);
  const hasActiveFilters = !!searchTerm || !!filterStatus;

  return (
    <div className="entity-page">
      <MyShiftStatusBanner category="seller_application" />
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت درخواست‌های فروشندگی</h1>
          <p className="entity-subtitle">بررسی و تایید درخواست‌های ثبت فروشگاه جدید</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={() => { fetchApplications(); fetchStats(); }} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><StoreIcon /></div>
          <div>
            <p className="entity-stat-label">کل درخواست‌ها</p>
            <p className="entity-stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><HourglassEmptyIcon /></div>
          <div>
            <p className="entity-stat-label">در انتظار</p>
            <p className="entity-stat-value">{stats.pending}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">تایید شده</p>
            <p className="entity-stat-value">{stats.approved}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">رد شده</p>
            <p className="entity-stat-value">{stats.rejected}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی نام فروشگاه..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as ApplicationStatus | ''); setPage(0); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value={ApplicationStatus.PENDING}>در انتظار بررسی</option>
              <option value={ApplicationStatus.APPROVED}>تایید شده</option>
              <option value={ApplicationStatus.REJECTED}>رد شده</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {filterStatus && (
              <span className="filter-tag">
                وضعیت: {statusLabels[filterStatus]}
                <button onClick={() => setFilterStatus('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && !applications.length ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : error ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InboxIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">{error}</p>
            <button className="header-btn primary" onClick={fetchApplications} style={{ marginTop: 12 }}>
              <RefreshIcon fontSize="small" /> تلاش مجدد
            </button>
          </div>
        ) : applications.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InboxIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">درخواستی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>فروشگاه</th>
                  <th>ایمیل</th>
                  <th>مالک</th>
                  <th>حوزه فعالیت</th>
                  <th>وضعیت</th>
                  <th>مسئول</th>
                  <th>تاریخ</th>
                  <th style={{ width: 160 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {app.storeLogoUrl ? (
                            <img src={app.storeLogoUrl} alt={app.storeName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                          ) : (
                            <StoreIcon />
                          )}
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{app.storeName}</span>
                          {app.applicationKind === 'conversion' && (
                            <span className="entity-desc" style={{ color: 'var(--color-info-dark, #0b57d0)', fontWeight: 600 }}>
                              تبدیل حساب شخصی به فروشگاه
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td><span className="entity-desc">{app.storeEmail}</span></td>
                    <td><span className="entity-desc">{`${app.ownerFirstName || ''} ${app.ownerLastName || ''}`.trim() || '-'}</span></td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {app.activityAreas?.slice(0, 2).map((a) => (
                          <span key={a._id} className="tag tag-sm tag-active">{a.name}</span>
                        ))}
                        {(app.activityAreas?.length || 0) > 2 && (
                          <span className="tag tag-sm tag-inactive">+{app.activityAreas!.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td><span className={statusTagClass[app.status]}>{statusLabels[app.status]}</span></td>
                    <td><AssigneeCell value={(app as any).assignedTo} /></td>
                    <td><span className="entity-desc">{formatDate(app.createdAt)}</span></td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => openDetails(app)}>
                            <VisibilityIcon />
                          </button>
                        </Tooltip>
                        {app.status === ApplicationStatus.PENDING && (
                          <>
                            <Tooltip title="تایید" arrow>
                              <button className="action-btn toggle active" onClick={() => openApprove(app)}>
                                <CheckCircleIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title="رد" arrow>
                              <button className="action-btn delete" onClick={() => openReject(app)}>
                                <CancelIcon />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {applications.map((app) => (
                <div key={app._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      {app.storeLogoUrl ? (
                        <img src={app.storeLogoUrl} alt={app.storeName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                      ) : (
                        <StoreIcon />
                      )}
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{app.storeName}</div>
                      <div className="entity-card-desc">{app.storeEmail}</div>
                    </div>
                    <div className="entity-card-status">
                      <span className={statusTagClass[app.status]}>{statusLabels[app.status]}</span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => openDetails(app)}>
                      <VisibilityIcon fontSize="small" /> جزئیات
                    </button>
                    {app.status === ApplicationStatus.PENDING && (
                      <>
                        <button className="entity-card-btn" onClick={() => openApprove(app)}>
                          <CheckCircleIcon fontSize="small" /> تایید
                        </button>
                        <button className="entity-card-btn danger" onClick={() => openReject(app)}>
                          <CancelIcon fontSize="small" /> رد
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalItems}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page + 1} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 0}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={closeAll} maxWidth="md" fullWidth className="storeapp-details-dialog">
        <div className="edit-dialog-title">
          <span>جزئیات درخواست فروشگاه</span>
          <button className="details-close" onClick={closeAll}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {currentApp && (
            <div className="edit-form">
              {/* Applicant */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات متقاضی</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام و نام خانوادگی</label>
                      <input type="text" readOnly value={`${currentApp.ownerFirstName || ''} ${currentApp.ownerLastName || ''}`.trim() || '-'} />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <input type="text" readOnly value={currentApp.storeEmail || '-'} />
                    </div>
                    {currentApp.storePhone && (
                      <div className="edit-field">
                        <label>تلفن</label>
                        <input type="text" readOnly value={currentApp.storePhone} />
                      </div>
                    )}
                    {currentApp.nationalId && (
                      <div className="edit-field">
                        <label>کد ملی</label>
                        <input type="text" readOnly value={currentApp.nationalId} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {currentApp.applicationKind === 'conversion' && (
                <div className="edit-card" style={{ borderColor: 'var(--color-info-dark, #0b57d0)' }}>
                  <div className="edit-card-header">تبدیل حساب فروشندهٔ شخصی به فروشگاه</div>
                  <div className="edit-card-body" style={{ fontSize: 13, lineHeight: 1.9 }}>
                    این متقاضی هم‌اکنون «فروشندهٔ شخصی» فعال است. با تأیید، <strong>همان حساب موجود</strong> به فروشگاه با نام و لوگوی
                    زیر تبدیل می‌شود؛ محصولات، سفارش‌ها، موجودی و امتیازهای او حفظ می‌شوند و فروشگاه جدیدی ساخته نمی‌شود.
                    {currentApp.conversionOfStoreId && (
                      <div style={{ marginTop: 6, color: 'var(--color-text-secondary)', fontSize: 12 }}>
                        شناسهٔ حساب فعلی: <code>{currentApp.conversionOfStoreId}</code>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Store Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات فروشگاه</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام فروشگاه</label>
                      <input type="text" readOnly value={currentApp.storeName || '-'} />
                    </div>
                    <div className="edit-field">
                      <label>تاریخ درخواست</label>
                      <input type="text" readOnly value={formatDate(currentApp.createdAt)} />
                    </div>
                    {currentApp.businessInfo && (
                      <div className="edit-field full">
                        <label>اطلاعات کسب‌وکار</label>
                        <textarea readOnly value={currentApp.businessInfo} rows={3} />
                      </div>
                    )}
                    <div className="edit-field full">
                      <label>حوزه‌های فعالیت</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 0' }}>
                        {currentApp.activityAreas?.length ? currentApp.activityAreas.map(a => (
                          <span key={a._id} className="tag tag-sm tag-active">{a.name}</span>
                        )) : <span style={{ color: 'var(--color-text-disabled)', fontSize: 13 }}>انتخاب نشده</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="edit-card">
                <div className="edit-card-header">تصاویر و مدارک</div>
                <div className="edit-card-body">
                  {currentApp.storeLogoUrl ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                      <img
                        src={currentApp.storeLogoUrl}
                        alt="لوگو فروشگاه"
                        style={{ maxWidth: 240, maxHeight: 240, borderRadius: 8, border: '1px solid var(--color-border)', cursor: 'zoom-in' }}
                        onClick={() => setImagePreviewOpen(true)}
                      />
                      <button className="edit-btn cancel" onClick={() => setImagePreviewOpen(true)}>
                        <ZoomInIcon fontSize="small" /> بزرگنمایی
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 24, color: 'var(--color-text-disabled)' }}>
                      <NoPhotographyIcon style={{ fontSize: 40 }} />
                      <span style={{ fontSize: 13 }}>تصویری آپلود نشده است</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="edit-card">
                <div className="edit-card-header">وضعیت</div>
                <div className="edit-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>وضعیت فعلی:</span>
                      <span className={statusTagClass[currentApp.status]}>{statusLabels[currentApp.status]}</span>
                    </div>
                    {currentApp.status === ApplicationStatus.REJECTED && currentApp.rejectionReason && (
                      <div className="edit-field">
                        <label>دلیل رد</label>
                        <textarea readOnly value={currentApp.rejectionReason} rows={2} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeAll}>بستن</button>
          {currentApp?.status === ApplicationStatus.PENDING && (
            <>
              <button
                className="edit-btn cancel"
                style={{ color: 'var(--color-error-dark)', borderColor: 'var(--color-error-dark)' }}
                onClick={() => { setDetailsOpen(false); openReject(currentApp); }}
              >
                <CancelIcon fontSize="small" /> رد درخواست
              </button>
              <button
                className="edit-btn submit"
                style={{ background: 'var(--color-success-dark)' }}
                onClick={() => { setDetailsOpen(false); openApprove(currentApp); }}
              >
                <CheckCircleIcon fontSize="small" /> تایید درخواست
              </button>
            </>
          )}
        </div>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveOpen} onClose={closeAll} maxWidth="xs" fullWidth className="storeapp-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: 'var(--color-success-bg)' }}>
            <CheckCircleIcon style={{ fontSize: 28, color: 'var(--color-success-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">تایید درخواست فروشگاه</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-success-dark)' }}>
              <CheckCircleIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <p className="delete-dialog-text">
              آیا از تایید درخواست <strong>«{currentApp?.storeName}»</strong> اطمینان دارید؟
              <br />با تایید، فروشگاه جدید ایجاد می‌شود.
            </p>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={isApproving}>انصراف</button>
            <button
              className="btn btn-danger"
              style={{ background: 'var(--color-success-dark)' }}
              onClick={handleApprove}
              disabled={isApproving || !!operationSuccess}
            >
              {isApproving && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CheckCircleIcon fontSize="small" /> تایید
            </button>
          </div>
        </div>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectOpen} onClose={closeAll} maxWidth="xs" fullWidth className="storeapp-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon">
            <CancelIcon style={{ fontSize: 28, color: 'var(--color-error-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">رد درخواست فروشگاه</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-error-dark)' }}>
              <CancelIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                دلیل رد درخواست <strong>«{currentApp?.storeName}»</strong> را وارد کنید.
                <br />این دلیل به کاربر نمایش داده می‌شود.
              </p>
              <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="دلیل رد را وارد کنید..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${!rejectionReason.trim() ? 'var(--color-error-dark)' : 'var(--color-border)'}`,
                    borderRadius: 8,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-paper)',
                    color: 'var(--color-text-primary)'
                  }}
                />
                {!rejectionReason.trim() && (
                  <span style={{ color: 'var(--color-error-dark)', fontSize: 12, marginTop: 4, display: 'block' }}>
                    وارد کردن دلیل الزامی است
                  </span>
                )}
              </div>
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={isRejecting}>انصراف</button>
            <button
              className="btn btn-danger"
              onClick={handleReject}
              disabled={isRejecting || !rejectionReason.trim() || !!operationSuccess}
            >
              {isRejecting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CancelIcon fontSize="small" /> رد درخواست
            </button>
          </div>
        </div>
      </Dialog>

      {/* Image Preview Overlay */}
      {imagePreviewOpen && currentApp?.storeLogoUrl && (
        <div
          onClick={() => setImagePreviewOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button
            onClick={() => setImagePreviewOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon />
          </button>
          <img
            src={currentApp.storeLogoUrl}
            alt="لوگو"
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default StoreApplicationsManagement;
